import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Wrench, Plus, Copy, X } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_STYLE = {
  up: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  down: 'bg-amber-100 text-amber-800 border-amber-300',
  none: 'bg-rose-100 text-rose-800 border-rose-300',
  unknown: 'bg-gray-100 text-gray-600 border-gray-300',
};

const label = (n, s) => {
  if (s === 'up') return `VPN${n} OK`;
  if (s === 'none') return `VPN${n} belum ada`;
  if (s === 'unknown') return `VPN${n} ?`;
  return n === 1 ? 'VPN1 DOWN' : 'VPN2 tunnel mati';
};

export const useDualVpn = () => {
  const [statusMap, setStatusMap] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [script, setScript] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/v2/dual-vpn/status`, { headers: authHeaders(), timeout: 30000 });
      if (r.data?.success) setStatusMap(r.data.data || {});
    } catch (e) {
      // endpoint belum ada di instance lama (mis. standby) — sembunyikan saja
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 120000);
    return () => clearInterval(t);
  }, [refresh]);

  const provision = async (router) => {
    const st = statusMap[String(router.id)];
    const action = st?.vpn2_user ? 'Perbaiki' : 'Buat';
    if (!window.confirm(`${action} VPN2 untuk router "${router.router_name}"?\nPelanggan online tidak akan terputus.`)) return;
    setBusyId(router.id);
    try {
      const r = await axios.post(`${API_URL}/api/v2/dual-vpn/${router.id}/provision`, {}, { headers: authHeaders(), timeout: 180000 });
      const d = r.data?.data || {};
      if (d.manual_required) {
        setScript({ router: router.router_name, ...d });
        toast.warning('API router tidak terjangkau — tempel script manual via Winbox');
      } else if (d.tunnel_running) {
        toast.success(`VPN2 aktif: ${d.vpn2_user} (${d.vpn2_ip})`);
      } else {
        setScript({ router: router.router_name, ...d });
        toast.warning('Konfigurasi terpasang, tapi tunnel VPN2 belum tersambung');
      }
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Gagal: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  return { statusMap, busyId, script, setScript, provision };
};

export const DualVpnStatusRow = ({ router, status, isAdmin, busy, onProvision }) => {
  if (!status) return null;
  const needAction = status.can_provision && (status.vpn2_status === 'none' || status.vpn2_status === 'down');
  const isFix = status.vpn2_status === 'down';
  if (status.vpn1_status === 'none' && status.vpn2_status === 'none') {
    return (
      <div className="flex items-center gap-2" data-testid={`dual-vpn-row-${router.id}`}>
        <span className="text-gray-500 w-16 dark:text-slate-400">VPN:</span>
        <Badge variant="outline" data-testid={`vpn-none-${router.id}`} className={STATUS_STYLE.unknown}>Tanpa VPN (IP publik)</Badge>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`dual-vpn-row-${router.id}`}>
      <span className="text-gray-500 w-16 dark:text-slate-400">VPN:</span>
      {status.vpn1_status !== 'none' && (
        <Badge variant="outline" data-testid={`vpn1-status-${router.id}`} title={`${status.vpn1_user || ''} ${status.vpn1_ip || ''}`}
          className={STATUS_STYLE[status.vpn1_status === 'down' ? 'unknown' : status.vpn1_status]}>
          {label(1, status.vpn1_status)}
        </Badge>
      )}
      <Badge variant="outline" data-testid={`vpn2-status-${router.id}`} title={`${status.vpn2_user || ''} ${status.vpn2_ip || ''}`}
        className={STATUS_STYLE[status.vpn2_status]}>
        {status.vpn2_status === 'up' && <ShieldCheck className="h-3 w-3 mr-1" />}
        {label(2, status.vpn2_status)}
      </Badge>
      {isAdmin && needAction && (
        <Button size="sm" variant="outline" disabled={busy}
          onClick={(e) => { e.stopPropagation(); onProvision(router); }}
          data-testid={`dual-vpn-${isFix ? 'fix' : 'create'}-btn-${router.id}`}
          className="h-6 px-2 text-[10px] border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:text-indigo-200 dark:border-indigo-400 dark:hover:bg-indigo-900/30">
          {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : isFix ? <Wrench className="h-3 w-3 mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
          {busy ? 'Memproses...' : isFix ? 'Perbaiki' : 'Buat VPN2'}
        </Button>
      )}
    </div>
  );
};

export const DualVpnScriptModal = ({ data, onClose }) => {
  if (!data) return null;
  const copy = () => { navigator.clipboard.writeText(data.script || ''); toast.success('Script berhasil dicopy!'); };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="dual-vpn-result-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 dark:bg-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-800 dark:text-slate-200">VPN2 — {data.router}</h3>
            <p className="text-sm text-gray-500 dark:text-slate-400">{data.vpn2_user} → {data.vpn2_ip}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="dual-vpn-modal-close"><X className="h-5 w-5" /></button>
        </div>
        <ul className="text-xs space-y-1 mb-4 text-gray-700 dark:text-slate-300" data-testid="dual-vpn-steps">
          {(data.steps || []).map((s, i) => <li key={i}>• {s}</li>)}
        </ul>
        {data.script && (
          <>
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 dark:bg-amber-900/30 dark:text-amber-200">
              API router tidak bisa diakses dari server. Buka Winbox → New Terminal → paste script ini.
            </p>
            <pre className="bg-slate-900 p-4 text-xs text-green-400 overflow-x-auto font-mono whitespace-pre-wrap rounded-lg" data-testid="dual-vpn-script">{data.script}</pre>
          </>
        )}
        <div className="mt-4 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>Tutup</Button>
          {data.script && (
            <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={copy} data-testid="dual-vpn-copy-script">
              <Copy className="w-4 h-4 mr-2" /> Copy Script
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
